import { create } from "zustand";
import { produce } from "immer";
import { devtools } from "zustand/middleware";
import _get from "lodash-es/get";
import { api } from "network/network";
import { APIendpoints } from "utils/constants/APIendpoints";
import { LS_KEYS } from "utils/constants/localStorageKeys";
import {   API_HOST_WMS } from "config/api.config";
import { isMobile } from "helpers/helper";

let useAuthStore = create(
  devtools((set, get) => ({
    authDetails: {
      isLoggedIn: false,
      isAuthorized: false,
      isAuthenticationInProgress: false,
    },
    loginDetails: {
      sendOtp: {
        begin: false,
        done: false,
        fail: false,
      },
      validateOtp: {
        begin: false,
        done: false,
        fail: false,
      },
      sellerHubHomePageData: {
        begin: false,
        done: false,
        fail: false,
      },
      sellerOnboarding: {
        begin: false,
        done: false,
        fail: false,
      },
      sellerCategoriesData: {
        begin: false,
        done: false,
        fail: false,
      }
    },
    error: null,

    getIsUserLoggedIn: () => !!get().authDetails.isLoggedIn,
    getIsUserAuthorized: () => !!get().authDetails.isAuthorized,
    getIsAuthenticationInProgress: () =>
      !!get().authDetails.isAuthenticationInProgress,
    setLoggedIn: (val) => {
      set(
        produce((state) => {
          state.authDetails.isLoggedIn = val;
          state.authDetails.isAuthorized = val;
        })
      );
    },
    setAuthorized: (val) =>
      set(
        produce((state) => {
          state.authDetails.isAuthorized = val;
        })
      ),
    setIsAuthenticationInProgress: (val) => {
      set(
        produce((state) => {
          state.authDetails.isAuthenticationInProgress = val;
        })
      );
    },

    setBegin: (key) =>
      set(
        produce((state) => {
          state.loginDetails[key].begin = true;
          state.loginDetails[key].done = false;
          state.loginDetails[key].fail = false;
        })
      ),
    setDone: (key) =>
      set(
        produce((state) => {
          state.loginDetails[key].begin = false;
          state.loginDetails[key].done = true;
          state.loginDetails[key].fail = false;
        })
      ),
    setFail: (key) =>
      set(
        produce((state) => {
          state.loginDetails[key].begin = false;
          state.loginDetails[key].done = false;
          state.loginDetails[key].fail = true;
        })
      ),

    setError: (err) =>
      set((state) => {
        state.error = err;
      }),

    sendOtp: async (request) => {
      get().setBegin("sendOtp");
      api
        .post(API_HOST_WMS, APIendpoints.sendOtp, request)
        .then(() => {
          get().setDone("sendOtp");
        })
        .catch((error) => {
          set({ error: _get(error, ["response", "data", "error"], {}) });
          get().setFail("sendOtp");
        });
    },

    validateOtp: async (request) => {
      get().setBegin("validateOtp");
      api
        .post(API_HOST_WMS, APIendpoints.validateOtp, request)
        .then(({ data, headers }) => {
          window.localStorage.setItem(LS_KEYS.OUTLET_ID, data.response.outlet_id);
          window.localStorage.setItem(LS_KEYS.OUTLET_NAME, data.response.outlet_name);
          window.localStorage.setItem(LS_KEYS.IS_NEW_SELLER_HUB_LOGIN_FLOW_ENABLED, data.response.is_new_seller_hub_login_flow_enabled);

          // Save user_id from response
          if (data.response.user_id) {
            window.localStorage.setItem(LS_KEYS.USER_ID, data.response.user_id);
          }

          // Save phone_number or email to localStorage based on what was used for login
          if (request.phone_no) {
            window.localStorage.setItem(LS_KEYS.PHONE_NUMBER_SIGNIN, request.phone_no);
            window.localStorage.removeItem(LS_KEYS.EMAIL);
          } else if (request.email) {
            window.localStorage.setItem(LS_KEYS.EMAIL, request.email);
            window.localStorage.removeItem(LS_KEYS.PHONE_NUMBER_SIGNIN);
          }

          if (headers) {
            let token = headers.authorization;
            window.localStorage.setItem(LS_KEYS.TOKEN, token);
          }
          get().setLoggedIn(true);

          get().setDone("validateOtp");
        })
        .catch((error) => {
          set({ error: _get(error, ["response", "data", "error"], {}) });
          get().setFail("validateOtp");
        });
    },

    sellerHubHomePageData: async () => {
      get().setBegin("sellerHubHomePageData");
      api
        .get(API_HOST_WMS, APIendpoints.sellerHubHomePage)
        .then((response) => {
          get().setDone("sellerHubHomePageData");
          set({ sellerHubData: response?.data?.response });
        })
        .catch((error) => {
          set({ error: _get(error, ["response", "data", "error"], {}) });
          get().setFail("sellerHubHomePageData");
        });
    },

    sellerOnboarding: async (request, successCallback) => {
      get().setBegin("sellerOnboarding");
      api
        .post(API_HOST_WMS, APIendpoints.sellerOnboarding, request)
        .then(({ response, headers }) => {
          get().setDone("sellerOnboarding");
          set({
            onBoardingFormData: {
              message: 'Thank you for your interest! We will reach out to you soon!',
              type: 'success'
            }
          });
          successCallback();
        })
        .catch((error) => {
          set({
            onBoardingFormData: {
              message: error.response.data.error.message,
              type: 'error'
            }
          });
          set({ error: _get(error, ["response", "data", "error"], {}) });
          get().setFail("sellerOnboarding");
        });
    },

    getSellerCategories: async () => {
      get().setBegin("sellerCategoriesData");
      const url = APIendpoints.sellerCategories + "?get_parent_categories_only=true"
      api
        .get(API_HOST_WMS, url)
        .then((response) => {
          get().setDone("sellerCategoriesData");
          set({ sellerCategories: response?.data?.response });
        })
        .catch((error) => {
          set({ error: _get(error, ["response", "data", "error"], {}) });
          get().setFail("sellerCategoriesData");
        });
    },

    signOut: async () => {
      const performCleanup = () => {
        window.localStorage.removeItem(LS_KEYS.TOKEN);
        window.localStorage.removeItem(LS_KEYS.OUTLET_ID);
        window.localStorage.removeItem(LS_KEYS.OUTLET_NAME);
        window.localStorage.removeItem(LS_KEYS.SELLER_TYPE);
        window.localStorage.removeItem(LS_KEYS.PHONE_NUMBER);
        window.localStorage.removeItem(LS_KEYS.EMAIL);
        window.localStorage.removeItem(LS_KEYS.USER_ID);
        window.localStorage.removeItem(LS_KEYS.IS_NEW_SELLER_HUB_LOGIN_FLOW_ENABLED);
        window.localStorage.removeItem(LS_KEYS.PHONE_NUMBER_SIGNIN);

        if (window.logoutChannelMessageHandler) {
          window.logoutChannelMessageHandler.postMessage("logout");
        }

        window.location.reload();
      };

      const phoneNumber = window.localStorage.getItem(LS_KEYS.PHONE_NUMBER_SIGNIN);
      const email = window.localStorage.getItem(LS_KEYS.EMAIL);
      const userId = window.localStorage.getItem(LS_KEYS.USER_ID);

      const payload = {
        forced_logout: false,
        user_type: "SELLER",
      };

      // Add user_id to payload if available
      if (userId) {
        payload.user_id = parseInt(userId);
      }

      // add phone_no or email to payload based on what's available
      if (phoneNumber) {
        payload.phone_no = phoneNumber;
      } else if (email) {
        payload.email = email;
      } else {
        payload.phone_no = "";
      }

      api
        .post(API_HOST_WMS, APIendpoints.signout, payload)
        .finally(() => {
          performCleanup();
        });
    },
  }))
);

export default useAuthStore;
